import { useMutation } from '@tanstack/react-query';
import API from 'api'; // Adjust this import based on your API setup

type TrayIds = { kitInstanceId?: string; trayInstanceId?: string };
type Props = {
  deliveryId?: string;
  tray?: TrayIds;
};

export const usePrintAllTrayImages = (_deliveryId?: string, _tray?: TrayIds) => {
  const query = useMutation({
    mutationFn: async (props?: Props) => {
      const deliveryId = props?.deliveryId || _deliveryId;
      const tray = props?.tray || _tray;
      let response;

      // API call based on deliveryId and tray details
      if (deliveryId && tray?.kitInstanceId && tray.trayInstanceId) {
        response = await API.deliveries.getTrayCapturePrintOut({
          deliveryId,
          tray: { kitInstanceId: tray?.kitInstanceId, trayInstanceId: tray.trayInstanceId },
        });
      } else if (deliveryId && tray?.kitInstanceId) {
        response = await API.deliveries.getTrayCapturePrintOut({
          deliveryId,
          tray: { kitInstanceId: tray?.kitInstanceId },
        });
      } else if (deliveryId) {
        response = await API.deliveries.getTrayCapturePrintOut({
          deliveryId,
        });
      } else {
        throw new Error('deliveryId is required');
      }

      // Handling response as Blob
      let pdfBlob: Blob;
      if (response instanceof Blob) {
        pdfBlob = response;
      } else if (response && (response as any).data instanceof Blob) {
        pdfBlob = (response as any).data;
      } else {
        throw new Error('Unexpected response format');
      }

      if (!(pdfBlob instanceof Blob)) {
        throw new Error('Response is not a Blob');
      }

      return pdfBlob;
    },
  });

  // Function to manually trigger the query and open the PDF in a new tab
  const openTrayImageInNewTab = async (props?: Props) => {
    const result = await query.mutateAsync(props); // Wait for the refetch to complete

    // Ensure the query fetched data successfully
    if (result) {
      const pdfUrl = URL.createObjectURL(result);
      window.open(pdfUrl, '_blank'); // Open the PDF in a new tab
    } else {
      throw new Error('No data available to open PDF'); // Throw error if no data is available
    }
  };

  return {
    openTrayImageInNewTab,
    loading: query.isLoading,
    error: query.error,
  };
};

export default usePrintAllTrayImages;
